import { ScreenCardInView } from 'containers/Intelligence/filters/screenFilterSignature';

import { ORBIT_SCREEN_CUSTOMER_360, ORBIT_SCREEN_CUSTOMER_INSIGHTS } from './constants';
import { TOrbit } from './i18n/TModules';

export type OrbitStarterRequirement = 'reports' | 'segmentDimension';

export interface OrbitPromptStarter {
  id: string;
  labelKey: string;
  requires?: OrbitStarterRequirement;
}

export interface OrbitStarterGrants {
  reports: boolean;
  /** Whether anything on screen can actually be broken down by segment; see below. */
  segmentDimension: boolean;
}

const starterKey = (path: string) => `${TOrbit}.starters.${path}`;

export const ORBIT_GLOBAL_STARTERS: OrbitPromptStarter[] = [
  { id: 'global.changed', labelKey: starterKey('global.changed') },
  { id: 'global.attention', labelKey: starterKey('global.attention') },
  { id: 'global.why', labelKey: starterKey('global.why') },
  { id: 'global.trend', labelKey: starterKey('global.trend') },
  { id: 'global.report', labelKey: starterKey('global.report'), requires: 'reports' },
  { id: 'global.next', labelKey: starterKey('global.next') }
];

/*
  TODO(AF-5): the contextual starter copy is assumed from the FR §4.3.1 examples, pending product
  confirmation. §4.3.1 lets the dashboard select STATIC starters by "the current supported screen or
  selected metric" and forbids the backend generating any, so both the screen-keyed list below and
  the card-keyed one are dashboard-owned catalogues. PLA-8844 / PLA-9563 §2.
*/
export const ORBIT_CONTEXTUAL_STARTERS: Record<string, OrbitPromptStarter[]> = {
  [ORBIT_SCREEN_CUSTOMER_INSIGHTS]: [
    { id: 'customerInsights.trend', labelKey: starterKey('customerInsights.trend') },
    {
      id: 'customerInsights.attention',
      labelKey: starterKey('customerInsights.attention'),
      // "Which segments need attention?" asks for a per-segment breakdown, so it is offered only
      // where something on screen actually has segments to break down (PLA-9563 §2c).
      requires: 'segmentDimension'
    },
    {
      id: 'customerInsights.report',
      labelKey: starterKey('customerInsights.report'),
      requires: 'reports'
    }
  ],
  [ORBIT_SCREEN_CUSTOMER_360]: [
    { id: 'customer360.engagement', labelKey: starterKey('customer360.engagement') },
    { id: 'customer360.risk', labelKey: starterKey('customer360.risk') }
  ]
};

/**
 * The families the registry's cards fall into for the purpose of suggesting a question.
 *
 * A family rather than one entry per card: there are 27 Customer Insights cards and 23 Customer 360
 * ones, the two registries share most of their subject matter, and inventing fifty separate prompts
 * would promise precision nothing has checked. What a reader wants to ask about "Customer Engagement"
 * and about "Engagement Timeline" is the same question.
 */
export type OrbitCardFamily =
  | 'lifecycle'
  | 'engagement'
  | 'behaviour'
  | 'value'
  | 'risk'
  | 'readiness'
  | 'sentiment'
  | 'dataQuality';

/**
 * Registry card key -> family. Keys are `registry/cards.ts` and `registry/customer360Cards.ts`
 * card keys, which is what `ScreenCardInView.key` carries. A card absent from this map contributes
 * no starter, which is the honest answer for one whose subject has no prompt written for it.
 */
export const ORBIT_CARD_FAMILIES: Record<string, OrbitCardFamily> = {
  // Customer Insights
  customer_population_activity: 'lifecycle',
  customer_lifecycle_holdings: 'lifecycle',
  data_trust_integration_health: 'dataQuality',
  merchant_category_data_quality: 'dataQuality',
  customer_engagement: 'engagement',
  engagement_funnel: 'engagement',
  reward_loyalty_intelligence: 'engagement',
  customer_behaviour_trends: 'behaviour',
  payment_behaviour_cash_usage: 'behaviour',
  merchant_category_preferences: 'behaviour',
  wallet_activity_funding: 'behaviour',
  plan_usage_revenue: 'behaviour',
  current_predicted_customer_value: 'value',
  debt_spending_health: 'value',
  income_cashflow_health: 'value',
  customer_sentiment_expectations: 'sentiment',
  risk_unusual_activity: 'risk',
  churn_service_experience: 'risk',
  payment_usage_risk: 'risk',
  product_readiness: 'readiness',
  ready_for_engagement: 'readiness',
  growth_planner_handoff_readiness: 'readiness',
  growth_opportunities_product_interest: 'readiness',
  recharge_upgrade_readiness: 'readiness',
  renewal_growth_opportunities: 'readiness',
  policy_health_claims: 'risk',
  contact_restrictions: 'dataQuality',
  // Customer 360
  customer_details: 'lifecycle',
  profile_summary: 'lifecycle',
  holdings_customer_value: 'value',
  financial_health: 'value',
  customer_behaviour_profile: 'behaviour',
  cash_usage_dependency: 'behaviour',
  payment_merchant_behaviour: 'behaviour',
  wallet_activity_adoption: 'behaviour',
  engagement_timeline: 'engagement',
  rewards_loyalty_activity: 'engagement',
  sentiment_expectations: 'sentiment',
  data_confidence_supporting_evidence: 'dataQuality',
  transaction_data_quality: 'dataQuality',
  renewal_coverage_opportunities: 'readiness',
  plan_usage_upgrade_readiness: 'readiness',
  customer_opportunities_product_interest: 'readiness'
};

/** One starter per family, keyed the same way the screen-level ones are. TODO(AF-5) as above. */
export const ORBIT_FAMILY_STARTERS: Record<OrbitCardFamily, OrbitPromptStarter> = {
  lifecycle: { id: 'card.lifecycle', labelKey: starterKey('card.lifecycle') },
  engagement: { id: 'card.engagement', labelKey: starterKey('card.engagement') },
  behaviour: { id: 'card.behaviour', labelKey: starterKey('card.behaviour') },
  value: { id: 'card.value', labelKey: starterKey('card.value') },
  risk: { id: 'card.risk', labelKey: starterKey('card.risk') },
  readiness: { id: 'card.readiness', labelKey: starterKey('card.readiness') },
  sentiment: { id: 'card.sentiment', labelKey: starterKey('card.sentiment') },
  dataQuality: { id: 'card.dataQuality', labelKey: starterKey('card.dataQuality') }
};

/**
 * The cards that actually carry a segment, lifecycle or funnel-stage dimension, and so can answer a
 * question asked per segment. Each entry is the registry's own evidence, not a guess:
 *
 * - `customer_population_activity` declares `breakdownKey: 'customer_segment_distribution'`
 *   (`registry/cards.ts`).
 * - `customer_lifecycle_holdings` draws the lifecycle counts in its stacked bar; its own descriptor
 *   comment says so ("puts the lifecycle counts in the stacked bar instead").
 * - `engagement_funnel` is a funnel and names `final_stage_customers` among its metrics.
 * - Customer 360's `profile_summary` names `lifecycle_stage` and `engagement_timeline`
 *   `funnel_stage`.
 *
 * Everything else on either screen answers over the whole population, so a per-segment question put
 * to it is one Orbit has to come back and ask for a clarification about (PLA-9563 §2c).
 */
export const ORBIT_SEGMENT_DIMENSION_CARDS: ReadonlySet<string> = new Set([
  'customer_population_activity',
  'customer_lifecycle_holdings',
  'engagement_funnel',
  'profile_summary',
  'engagement_timeline'
]);

export const hasSegmentDimension = (cards: ScreenCardInView[]): boolean =>
  cards.some(card => ORBIT_SEGMENT_DIMENSION_CARDS.has(card.key));

/**
 * How many contextual starters a context may contribute. The idle panel already carries the six
 * global ones above them, and a screenful of four cards from four families would otherwise push the
 * list to thirteen rows.
 */
export const ORBIT_CONTEXTUAL_STARTER_LIMIT = 4;

/**
 * The starters for the context the reader is in: the families of the cards they actually have on
 * screen first, in reading order, then the screen's own. Card-first because the cards are the more
 * specific claim and because the two chips drawn under an answer take the head of this list - which
 * is what makes them follow the screenful instead of repeating one pair for the whole visit
 * (PLA-9563 §2a).
 */
export const getContextualStarters = (
  screen?: string,
  cards: ScreenCardInView[] = []
): OrbitPromptStarter[] => {
  const screenStarters =
    screen !== undefined && Object.prototype.hasOwnProperty.call(ORBIT_CONTEXTUAL_STARTERS, screen)
      ? ORBIT_CONTEXTUAL_STARTERS[screen]
      : [];

  if (screenStarters.length === 0) {
    return [];
  }

  const families: OrbitCardFamily[] = [];

  cards.forEach(card => {
    const family = ORBIT_CARD_FAMILIES[card.key];

    if (family !== undefined && !families.includes(family)) {
      families.push(family);
    }
  });

  const seen = new Set<string>();

  return [...families.map(family => ORBIT_FAMILY_STARTERS[family]), ...screenStarters]
    .filter(starter => {
      if (seen.has(starter.id)) {
        return false;
      }

      seen.add(starter.id);

      return true;
    })
    .slice(0, ORBIT_CONTEXTUAL_STARTER_LIMIT);
};

export const isStarterAllowed = (
  starter: OrbitPromptStarter,
  grants: OrbitStarterGrants
): boolean => starter.requires === undefined || grants[starter.requires];

/**
 * The comparable form of a question: a starter already asked in this conversation must not be
 * offered again, and the two sides being compared are a catalogue label and whatever the transcript
 * echoed back, so trailing punctuation and casing cannot be part of the identity (PLA-9563 §2b).
 */
export const orbitQuestionKey = (question: string): string =>
  question
    .trim()
    .toLowerCase()
    .replace(/[.?!\s]+$/, '');

/** Drops every starter whose question this conversation has already put to Orbit. */
export const withoutAskedStarters = (
  starters: OrbitPromptStarter[],
  label: (starter: OrbitPromptStarter) => string,
  asked: ReadonlySet<string>
): OrbitPromptStarter[] => starters.filter(starter => !asked.has(orbitQuestionKey(label(starter))));
